import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, PlusCircle, Edit3 } from "lucide-react";

/**
 * Surfaces when the operator picks a Delivery customer that already
 * has at least one ACTIVE order (NEW / DISPATCHED). The choice gates
 * the rest of the flow: either start a fresh order (current behaviour)
 * or jump into the pending order in edit-mode so the new items are
 * appended.
 *
 * Props:
 *   • open / onOpenChange
 *   • customerName  — for the heading line
 *   • pendingOrders — list of {order_no, total, items_count, created_at, status}
 *   • onNew    — callback for «Νέα Παραγγελία»
 *   • onAppend(order) — callback for «Προσθήκη στην υπάρχουσα»; receives
 *                       the order to switch into edit-mode.
 */
export default function CustomerPendingOrderDialog({
  open,
  onOpenChange,
  customerName,
  pendingOrders = [],
  onNew,
  onAppend,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-xl p-0 gap-0 overflow-hidden"
        data-testid="pending-order-dialog"
      >
        {/* Header */}
        <div className="px-6 py-5 bg-amber-50 border-b-2 border-amber-200 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center shrink-0">
            <AlertCircle size={26} className="text-amber-700" />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg font-bold text-amber-900">
              Ο πελάτης έχει εκκρεμή παραγγελία
            </div>
            <div className="text-xs text-slate-700 mt-0.5">
              <span className="font-bold">{customerName}</span> έχει{" "}
              <span className="font-bold">{pendingOrders.length}</span>{" "}
              {pendingOrders.length === 1 ? "ενεργή παραγγελία" : "ενεργές παραγγελίες"} σε αναμονή.
            </div>
          </div>
        </div>

        {/* Pending orders list */}
        <div className="px-6 py-3 bg-white max-h-[40vh] overflow-y-auto">
          <div className="space-y-2">
            {pendingOrders.map((po) => (
              <div
                key={po.id}
                className="rounded border-2 border-slate-200 px-3 py-2 flex items-center justify-between gap-3"
                data-testid={`pending-order-row-${po.order_no}`}
              >
                <div className="min-w-0">
                  <div className="font-mono font-bold text-slate-800">
                    #{po.order_no}
                    {po.code ? (
                      <span className="ml-1.5 text-[10px] text-slate-500">
                        κωδ. {String(po.code).padStart(4, "0")}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-500">
                    {(po.items || []).length} είδη ·{" "}
                    <span className="font-bold">{po.status}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-emerald-700 text-base">
                    {Number(po.total || 0).toFixed(2)} €
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => onAppend?.(po)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-3 text-xs font-bold shrink-0"
                  data-testid={`pending-order-append-${po.order_no}`}
                >
                  <Edit3 size={13} className="mr-1.5" />
                  Προσθήκη
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer / primary actions */}
        <div className="px-6 py-4 border-t-2 border-slate-200 bg-slate-50 flex gap-3 justify-end">
          <Button
            type="button"
            onClick={onNew}
            className="h-12 px-5 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="pending-order-new-btn"
          >
            <PlusCircle size={18} className="mr-2" />
            Νέα Παραγγελία
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
